import React, { forwardRef, PropsWithChildren } from 'react';

import { filterObject } from './Utilities';
import { Link } from './Link';

export interface OptionalLinkProps extends PropsWithChildren {
  /** Link URL */
  href?: string;
  /** Title on link */
  title?: string;
  /** Button if no link */
  button?: boolean;
  /** Link target */
  target?: HTMLAnchorElement['target'];
  /** Div if no link and no button */
  div?: boolean;
  /** Span if no link and no button */
  span?: boolean;
  /** Additional class */
  className?: string;
  /** Optional rel on link */
  rel?: string;
  /** Optional click handler */
  onClick?: (e: MouseEvent) => void;
  /** Optional type of button */
  type?: string;
  /** Optional disabled button */
  disabled?: boolean;
  /** Optional role */
  role?: string;
  [name: `aria-${string}`]: string | boolean;
  [name: `data-${string}`]: string | boolean;
}

export type LinkComponentProps = React.ComponentPropsWithoutRef<'a'> &
  React.RefAttributes<HTMLAnchorElement>;

// let LinkComponent accept both FC and forwardRef components
let LinkComponent: React.ComponentType<LinkComponentProps> =
  Link as unknown as React.ComponentType<LinkComponentProps>;

// eslint-disable-next-line react-refresh/only-export-components
export const registerLinkComponent = (
  component: React.ComponentType<LinkComponentProps>,
) => {
  LinkComponent = component;
};

/** An optional link */
export const OptionalLink = forwardRef(
  (
    { href, button, div, span, children, ...props }: OptionalLinkProps,
    ref?: any,
  ) => {
    // Props that are valid for all elements
    const commonAllowedProps = [
      'title',
      'className',
      'onClick',
      'role',
      'disabled',
    ];

    // Props that are only valid for links
    const linkOnlyProps = ['href', 'target', 'rel'];

    // Props that are only valid for buttons
    const buttonOnlyProps = ['type'];

    if (!href || props?.disabled) {
      if (
        (button && !div && !span) ||
        props.onClick ||
        (props?.disabled && href)
      ) {
        const filteredProps = filterObject(
          props,
          [...commonAllowedProps, ...buttonOnlyProps],
          [],
          /(aria|data)-.*/,
        );
        return (
          <button ref={ref} disabled={props?.disabled} {...filteredProps}>
            {children}
          </button>
        );
      }
      if (div) {
        const filteredProps = filterObject(
          props,
          commonAllowedProps,
          [],
          /(aria|data)-.*/,
        );
        return (
          <div ref={ref} {...filteredProps}>
            {children}
          </div>
        );
      }
      if (span) {
        const filteredProps = filterObject(
          props,
          commonAllowedProps,
          [],
          /(aria|data)-.*/,
        );
        return (
          <span ref={ref} {...filteredProps}>
            {children}
          </span>
        );
      }
      return <>{children}</>;
    }

    const filteredProps = filterObject(
      props,
      [...commonAllowedProps, ...linkOnlyProps],
      [],
      /(aria|data)-.*/,
    );
    return (
      <LinkComponent ref={ref} href={href} {...filteredProps}>
        {children}
      </LinkComponent>
    );
  },
);
